import React, { useState, useEffect } from 'react';
import './TeacherDashboard.css';

const TeacherDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('courses');
  const [myCourses, setMyCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);

  useEffect(() => {
    const courses = JSON.parse(localStorage.getItem('courses') || '[]');
    const allStudents = JSON.parse(localStorage.getItem('students') || '[]');
    
    const teacherCourses = courses.filter(course => 
      course.teacher && course.teacher.id === user.id
    );
    
    setMyCourses(teacherCourses);

    const enrolledStudents = allStudents.filter(student => 
      student.courses?.some(studentCourse => 
        teacherCourses.some(teacherCourse => teacherCourse.id === studentCourse.id)
      )
    );
    
    setStudents(enrolledStudents);
  }, [user]);

  return (
    <div className="teacher-dashboard">
      <header className="teacher-header">
        <div className="header-left">
          <h1>Teacher Dashboard</h1>
          <p>Welcome, {user.name} ({user.email})</p>
        </div>
        <button onClick={onLogout} className="logout-btn">Logout</button>
      </header>

      <nav className="teacher-nav">
        <button 
          className={activeTab === 'courses' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('courses')}
        >
          My Courses
        </button>
        <button 
          className={activeTab === 'attendance' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('attendance')}
        >
          Attendance
        </button>
        <button 
          className={activeTab === 'marks' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveTab('marks')}
        >
          Marks Management
        </button>
      </nav>

      <main className="teacher-content">
        {activeTab === 'courses' && (
          <CoursesTab 
            myCourses={myCourses} 
            students={students}
            onSelectCourse={setSelectedCourse}
          />
        )}

        {activeTab === 'attendance' && (
          <AttendanceTab 
            myCourses={myCourses}
            students={students}
            selectedCourse={selectedCourse}
            onSelectCourse={setSelectedCourse}
          />
        )}

        {activeTab === 'marks' && (
          <MarksTab 
            myCourses={myCourses}
            students={students}
            selectedCourse={selectedCourse}
            onSelectCourse={setSelectedCourse}
          />
        )}
      </main>
    </div>
  );
};

// Courses Tab Component (same as before)
const CoursesTab = ({ myCourses, students, onSelectCourse }) => {
  return (
    <div className="tab-content">
      <h2>My Courses</h2>
      {myCourses.length === 0 ? (
        <div className="no-data-card">
          <h3>No Courses Assigned</h3>
          <p>You haven't been assigned any courses yet.</p>
          <p>Please ask the admin to assign courses to you.</p>
        </div>
      ) : (
        <div className="courses-list">
          {myCourses.map(course => (
            <div key={course.id} className="course-card">
              <h3>{course.code} - {course.name}</h3>
              <p><strong>Credit Hours:</strong> {course.creditHours}</p>
              <p><strong>Enrolled Students:</strong> {
                students.filter(student => 
                  student.courses?.some(c => c.id === course.id)
                ).length
              }</p>
              <div className="course-actions">
                <button 
                  onClick={() => onSelectCourse(course)}
                  className="action-btn primary"
                >
                  Manage This Course
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Attendance Tab Component (same as before)
const AttendanceTab = ({ myCourses, students, selectedCourse, onSelectCourse }) => {
  const [classSessions, setClassSessions] = useState([]);
  const [newSessionDate, setNewSessionDate] = useState('');
  const [attendanceData, setAttendanceData] = useState({});

  useEffect(() => {
    const sessions = JSON.parse(localStorage.getItem('classSessions') || '[]');
    const courseSessions = sessions.filter(session => 
      session.courseId === selectedCourse?.id
    );
    setClassSessions(courseSessions);
    
    const attendance = JSON.parse(localStorage.getItem('attendance') || '{}');
    setAttendanceData(attendance);
  }, [selectedCourse]);

  const handleAddClassSession = () => {
    if (!newSessionDate) {
      alert('Please select a date');
      return;
    }

    const newSession = {
      id: Date.now().toString(),
      courseId: selectedCourse.id,
      date: newSessionDate,
      createdAt: new Date().toISOString()
    };

    const updatedSessions = [...classSessions, newSession];
    setClassSessions(updatedSessions);
    localStorage.setItem('classSessions', JSON.stringify(updatedSessions));
    setNewSessionDate('');
    alert('Class session added successfully!');
  };

  const handleMarkAttendance = (sessionId, studentId, status) => {
    const key = `${sessionId}_${studentId}`;
    const updatedAttendance = {
      ...attendanceData,
      [key]: status
    };
    
    setAttendanceData(updatedAttendance);
    localStorage.setItem('attendance', JSON.stringify(updatedAttendance));
  };

  // Updated attendance calculation with late policy (50% for late)
  const calculateStudentAttendance = (studentId) => {
    const courseSessions = classSessions.filter(session => session.courseId === selectedCourse.id);
    if (courseSessions.length === 0) return 0;

    let attendancePoints = 0;
    courseSessions.forEach(session => {
      const key = `${session.id}_${studentId}`;
      const status = attendanceData[key];
      if (status === 'P') {
        attendancePoints += 1; // Full point for present
      } else if (status === 'L') {
        attendancePoints += 0.5; // Half point for late
      }
      // Absent gets 0 points
    });

    return Math.round((attendancePoints / courseSessions.length) * 100);
  };

  const courseStudents = students.filter(student =>
    student.courses?.some(c => c.id === selectedCourse?.id)
  );

  if (!selectedCourse) {
    return (
      <div className="tab-content">
        <h2>Attendance Management</h2>
        <div className="course-selection">
          <h3>Select a Course to Manage Attendance</h3>
          <div className="courses-grid">
            {myCourses.map(course => (
              <div key={course.id} className="course-selection-card">
                <h4>{course.code} - {course.name}</h4>
                <p>Enrolled Students: {
                  students.filter(student => 
                    student.courses?.some(c => c.id === course.id)
                  ).length
                }</p>
                <button 
                  onClick={() => onSelectCourse(course)}
                  className="select-btn"
                >
                  Select Course
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="attendance-header">
        <h2>Attendance Management - {selectedCourse.code} - {selectedCourse.name}</h2>
        <button 
          onClick={() => onSelectCourse(null)}
          className="back-btn"
        >
          ← Back to Course Selection
        </button>
      </div>

      {/* Attendance Policy Notice */}
      <div className="policy-notice">
        <h4>📋 Attendance Policy:</h4>
        <ul>
          <li>✅ <strong>Present (P):</strong> 100% attendance credit</li>
          <li>⚠️ <strong>Late (L):</strong> 50% attendance credit</li>
          <li>❌ <strong>Absent (A):</strong> 0% attendance credit</li>
        </ul>
      </div>

      {/* Add Class Session */}
      <div className="session-management">
        <h3>Add Class Session</h3>
        <div className="session-form">
          <input
            type="date"
            value={newSessionDate}
            onChange={(e) => setNewSessionDate(e.target.value)}
            className="date-input"
          />
          <button onClick={handleAddClassSession} className="add-session-btn">
            Add Class Session
          </button>
        </div>
      </div>

      {/* Class Sessions List */}
      <div className="sessions-list">
        <h3>Class Sessions ({classSessions.length})</h3>
        {classSessions.length === 0 ? (
          <p className="no-sessions">No class sessions added yet</p>
        ) : (
          <div className="sessions-table">
            {classSessions.map(session => (
              <div key={session.id} className="session-card">
                <div className="session-header">
                  <h4>Session: {new Date(session.date).toLocaleDateString()}</h4>
                  <span className="session-id">ID: {session.id}</span>
                </div>
                
                <div className="attendance-marking">
                  <table>
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>Email</th>
                        <th>Attendance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {courseStudents.map(student => (
                        <tr key={student.id}>
                          <td>{student.name}</td>
                          <td>{student.email}</td>
                          <td>
                            <div className="attendance-buttons">
                              <button
                                className={`attendance-btn present ${attendanceData[`${session.id}_${student.id}`] === 'P' ? 'active' : ''}`}
                                onClick={() => handleMarkAttendance(session.id, student.id, 'P')}
                              >
                                P
                              </button>
                              <button
                                className={`attendance-btn absent ${attendanceData[`${session.id}_${student.id}`] === 'A' ? 'active' : ''}`}
                                onClick={() => handleMarkAttendance(session.id, student.id, 'A')}
                              >
                                A
                              </button>
                              <button
                                className={`attendance-btn late ${attendanceData[`${session.id}_${student.id}`] === 'L' ? 'active' : ''}`}
                                onClick={() => handleMarkAttendance(session.id, student.id, 'L')}
                              >
                                L
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Attendance Summary */}
      <div className="attendance-summary">
        <h3>Attendance Summary</h3>
        <table className="summary-table">
          <thead>
            <tr>
              <th>Student Name</th>
              <th>Total Classes</th>
              <th>Present</th>
              <th>Absent</th>
              <th>Late</th>
              <th>Attendance %</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {courseStudents.map(student => {
              const totalClasses = classSessions.length;
              let present = 0, absent = 0, late = 0;
              
              classSessions.forEach(session => {
                const key = `${session.id}_${student.id}`;
                const status = attendanceData[key];
                if (status === 'P') present++;
                else if (status === 'A') absent++;
                else if (status === 'L') late++;
              });

              const percentage = calculateStudentAttendance(student.id);
              const status = percentage >= 75 ? 'Good' : 'DEBAR';

              return (
                <tr key={student.id}>
                  <td>{student.name}</td>
                  <td>{totalClasses}</td>
                  <td className="present-count">{present}</td>
                  <td className="absent-count">{absent}</td>
                  <td className="late-count">{late}</td>
                  <td className={`percentage ${percentage >= 75 ? 'good' : 'poor'}`}>
                    {percentage}%
                  </td>
                  <td>
                    <span className={`status ${status.toLowerCase()}`}>
                      {status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Enhanced Marks Tab Component with Assessment Deletion and Fixed Marks Calculation
const MarksTab = ({ myCourses, students, selectedCourse, onSelectCourse }) => {
  const [marksData, setMarksData] = useState({});
  const [assessments, setAssessments] = useState([]);
  const [newAssessment, setNewAssessment] = useState({
    type: 'quiz',
    title: '',
    date: '',
    maxMarks: 15
  });

  useEffect(() => {
    const marks = JSON.parse(localStorage.getItem('marks') || '{}');
    setMarksData(marks);
    
    const allAssessments = JSON.parse(localStorage.getItem('assessments') || '[]');
    const courseAssessments = allAssessments.filter(assessment => 
      assessment.courseId === selectedCourse?.id
    );
    setAssessments(courseAssessments);
  }, [selectedCourse]);

  const handleAddAssessment = () => {
    if (!newAssessment.title || !newAssessment.date) {
      alert('Please fill all fields');
      return;
    }

    const assessment = {
      id: Date.now().toString(),
      courseId: selectedCourse.id,
      type: newAssessment.type,
      title: newAssessment.title,
      date: newAssessment.date,
      maxMarks: newAssessment.maxMarks,
      createdAt: new Date().toISOString()
    };

    const updatedAssessments = [...assessments, assessment];
    setAssessments(updatedAssessments);
    localStorage.setItem('assessments', JSON.stringify(updatedAssessments));
    
    setNewAssessment({
      type: 'quiz',
      title: '',
      date: '',
      maxMarks: 15
    });
    
    alert('Assessment added successfully!');
  };

  // NEW: Delete assessment functionality
  const handleDeleteAssessment = (assessmentId) => {
    if (window.confirm('Are you sure you want to delete this assessment? This will remove all marks associated with it.')) {
      // Remove assessment from assessments list
      const updatedAssessments = assessments.filter(assessment => assessment.id !== assessmentId);
      setAssessments(updatedAssessments);
      localStorage.setItem('assessments', JSON.stringify(updatedAssessments));

      // Remove all marks associated with this assessment
      const updatedMarks = { ...marksData };
      Object.keys(updatedMarks).forEach(key => {
        if (key.startsWith(`${assessmentId}_`)) {
          delete updatedMarks[key];
        }
      });
      
      setMarksData(updatedMarks);
      localStorage.setItem('marks', JSON.stringify(updatedMarks));
      
      alert('Assessment deleted successfully!');
    }
  };

  const handleUpdateMarks = (assessmentId, studentId, marks) => {
    const key = `${assessmentId}_${studentId}`;
    const updatedMarks = {
      ...marksData,
      [key]: parseInt(marks) || 0
    };
    
    setMarksData(updatedMarks);
  };

  const handleSaveAllMarks = () => {
    localStorage.setItem('marks', JSON.stringify(marksData));
    alert('All marks saved successfully!');
  };

  const getStudentMarksForAssessment = (assessmentId, studentId) => {
    const key = `${assessmentId}_${studentId}`;
    return marksData[key] || 0;
  };

  // FIXED: Correct total marks calculation
  const calculateTotalMarks = (studentId) => {
    let total = 0;
    let hasAssessments = false;
    
    // Get quiz marks (average of all quizzes) - 15% of total
    const quizzes = assessments.filter(a => a.type === 'quiz');
    if (quizzes.length > 0) {
      hasAssessments = true;
      const quizTotal = quizzes.reduce((sum, quiz) => 
        sum + getStudentMarksForAssessment(quiz.id, studentId), 0
      );
      const quizAverage = quizzes.length > 0 ? quizTotal / quizzes.length : 0;
      total += (quizAverage / 15) * 15; // 15% of total grade
    }
    
    // Get assignment marks (average of all assignments) - 15% of total
    const assignments = assessments.filter(a => a.type === 'assignment');
    if (assignments.length > 0) {
      hasAssessments = true;
      const assignmentTotal = assignments.reduce((sum, assignment) => 
        sum + getStudentMarksForAssessment(assignment.id, studentId), 0
      );
      const assignmentAverage = assignments.length > 0 ? assignmentTotal / assignments.length : 0;
      total += (assignmentAverage / 15) * 15; // 15% of total grade
    }
    
    // Get mid term marks - 30% of total
    const midTerms = assessments.filter(a => a.type === 'mid');
    if (midTerms.length > 0) {
      hasAssessments = true;
      const midTotal = midTerms.reduce((sum, mid) => 
        sum + getStudentMarksForAssessment(mid.id, studentId), 0
      );
      const midAverage = midTerms.length > 0 ? midTotal / midTerms.length : 0;
      total += (midAverage / 30) * 30; // 30% of total grade
    }
    
    // Get final exam marks - 40% of total
    const finals = assessments.filter(a => a.type === 'final');
    if (finals.length > 0) {
      hasAssessments = true;
      const finalTotal = finals.reduce((sum, final) => 
        sum + getStudentMarksForAssessment(final.id, studentId), 0
      );
      const finalAverage = finals.length > 0 ? finalTotal / finals.length : 0;
      total += (finalAverage / 40) * 40; // 40% of total grade
    }
    
    return hasAssessments ? Math.round(total) : 0;
  };

  const calculateGrade = (totalMarks) => {
    if (totalMarks >= 90) return 'A+';
    if (totalMarks >= 87) return 'A';
    if (totalMarks >= 84) return 'A-';
    if (totalMarks >= 80) return 'B+';
    if (totalMarks >= 76) return 'B';
    if (totalMarks >= 72) return 'B-';
    if (totalMarks >= 68) return 'C+';
    if (totalMarks >= 64) return 'C';
    if (totalMarks >= 60) return 'C-';
    if (totalMarks >= 50) return 'D';
    return 'F';
  };

  const courseStudents = students.filter(student =>
    student.courses?.some(c => c.id === selectedCourse?.id)
  );

  if (!selectedCourse) {
    return (
      <div className="tab-content">
        <h2>Marks Management</h2>
        <div className="course-selection">
          <h3>Select a Course to Manage Marks</h3>
          <div className="courses-grid">
            {myCourses.map(course => (
              <div key={course.id} className="course-selection-card">
                <h4>{course.code} - {course.name}</h4>
                <p>Enrolled Students: {
                  students.filter(student => 
                    student.courses?.some(c => c.id === course.id)
                  ).length
                }</p>
                <button 
                  onClick={() => onSelectCourse(course)}
                  className="select-btn"
                >
                  Select Course
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="marks-header">
        <h2>Marks Management - {selectedCourse.code} - {selectedCourse.name}</h2>
        <div className="header-actions">
          <button 
            onClick={() => onSelectCourse(null)}
            className="back-btn"
          >
            ← Back to Course Selection
          </button>
          <button onClick={handleSaveAllMarks} className="save-all-btn">
            💾 Save All Changes
          </button>
        </div>
      </div>

      {/* Add Assessment Section */}
      <div className="assessment-management">
        <h3>Add New Assessment</h3>
        <div className="assessment-form">
          <select 
            value={newAssessment.type}
            onChange={(e) => setNewAssessment({...newAssessment, type: e.target.value})}
            className="assessment-type"
          >
            <option value="quiz">Quiz</option>
            <option value="assignment">Assignment</option>
            <option value="mid">Mid Term</option>
            <option value="final">Final Exam</option>
          </select>
          
          <input
            type="text"
            placeholder="Assessment Title"
            value={newAssessment.title}
            onChange={(e) => setNewAssessment({...newAssessment, title: e.target.value})}
            className="assessment-title"
          />
          
          <input
            type="date"
            value={newAssessment.date}
            onChange={(e) => setNewAssessment({...newAssessment, date: e.target.value})}
            className="assessment-date"
          />
          
          <input
            type="number"
            placeholder="Max Marks"
            value={newAssessment.maxMarks}
            onChange={(e) => setNewAssessment({...newAssessment, maxMarks: parseInt(e.target.value) || 15})}
            className="assessment-marks"
            min="1"
            max="100"
          />
          
          <button onClick={handleAddAssessment} className="add-assessment-btn">
            Add Assessment
          </button>
        </div>
      </div>

      {/* Assessments List */}
      <div className="assessments-list">
        <h3>Assessments ({assessments.length})</h3>
        {assessments.length === 0 ? (
          <p className="no-assessments">No assessments added yet</p>
        ) : (
          <div className="assessments-grid">
            {assessments.map(assessment => (
              <div key={assessment.id} className="assessment-card">
                <div className="assessment-header">
                  <div className="assessment-title-section">
                    <h4>
                      <span className={`assessment-type-badge ${assessment.type}`}>
                        {assessment.type.toUpperCase()}
                      </span>
                      {assessment.title}
                    </h4>
                    <span className="assessment-date">
                      {new Date(assessment.date).toLocaleDateString()}
                    </span>
                  </div>
                  <button 
                    onClick={() => handleDeleteAssessment(assessment.id)}
                    className="delete-assessment-btn"
                    title="Delete Assessment"
                  >
                    🗑️ Delete
                  </button>
                </div>
                <p><strong>Max Marks:</strong> {assessment.maxMarks}</p>
                
                <div className="assessment-marks-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Marks</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {courseStudents.map(student => (
                        <tr key={student.id}>
                          <td>{student.name}</td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              max={assessment.maxMarks}
                              value={getStudentMarksForAssessment(assessment.id, student.id)}
                              onChange={(e) => handleUpdateMarks(assessment.id, student.id, e.target.value)}
                              className="marks-input"
                            />/{assessment.maxMarks}
                          </td>
                          <td>
                            <button 
                              className="save-btn"
                              onClick={() => {
                                localStorage.setItem('marks', JSON.stringify(marksData));
                                alert(`Marks saved for ${student.name}`);
                              }}
                            >
                              Save
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Overall Marks Summary */}
      <div className="overall-marks">
        <h3>Overall Course Marks</h3>
        {courseStudents.length === 0 ? (
          <p className="no-students">No students enrolled in this course</p>
        ) : (
          <div className="overall-marks-table">
            <table>
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>Email</th>
                  <th>Total Marks</th>
                  <th>Grade</th>
                </tr>
              </thead>
              <tbody>
                {courseStudents.map(student => {
                  const totalMarks = calculateTotalMarks(student.id);
                  const grade = calculateGrade(totalMarks);
                  
                  return (
                    <tr key={student.id}>
                      <td>{student.name}</td>
                      <td>{student.email}</td>
                      <td className="total-marks">
                        <strong>{totalMarks}/100</strong>
                        {assessments.length === 0 && (
                          <div className="no-marks-warning">No assessments added</div>
                        )}
                      </td>
                      <td>
                        <span className={`grade ${grade.toLowerCase()}`}>
                          {grade}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Weighting Information */}
        <div className="weighting-info">
          <h4>Grading Weight Distribution:</h4>
          <div className="weight-breakdown">
            <div className="weight-item">
              <span className="weight-type">Quizzes:</span>
              <span className="weight-percentage">15% of total grade</span>
            </div>
            <div className="weight-item">
              <span className="weight-type">Assignments:</span>
              <span className="weight-percentage">15% of total grade</span>
            </div>
            <div className="weight-item">
              <span className="weight-type">Mid Term:</span>
              <span className="weight-percentage">30% of total grade</span>
            </div>
            <div className="weight-item">
              <span className="weight-type">Final Exam:</span>
              <span className="weight-percentage">40% of total grade</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;